import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Shield, Lock, Eye, ChevronRight, Play, BarChart3,
  TrendingDown, Cpu, Zap, CheckCircle, ArrowRight,
  Globe, Database, Brain, Layers, Activity, FileText,
  Users, ShieldCheck, Server, Award
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0B3D91] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <span className="text-[#0B3D91] font-bold text-lg tracking-tight">ACCURIS</span>
            </div>
            <div className="h-6 w-px bg-gray-300" />
            <div className="flex items-center gap-1">
              <span className="text-green-600 font-bold text-lg">1BUY</span>
              <span className="text-gray-800 font-semibold text-lg">.AI</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
              data-testid="nav-demo-link"
            >
              See Demo
            </button>
            <button
              onClick={() => document.getElementById('security-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
              data-testid="nav-security-link"
            >
              Security
            </button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              size="sm"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 mr-2"
              data-testid="nav-login-btn"
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate('/cost-savings')}
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="nav-get-started-btn"
            >
              Get Started <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B3D91]/5 via-white to-green-50/60" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-green-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#0B3D91]/5 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24">
          <div className="flex items-center gap-2 mb-6">
            <Badge className="bg-green-100 text-green-700 border-0 text-xs font-medium px-3 py-1">
              Powered by 4B+ Data Points
            </Badge>
            <Badge className="bg-[#0B3D91]/10 text-[#0B3D91] border-0 text-xs font-medium px-3 py-1">
              Accuris Partnership
            </Badge>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.1] max-w-4xl mb-6" data-testid="hero-title">
            Welcome to the Most Important{' '}
            <span className="bg-gradient-to-r from-green-600 to-[#0B3D91] bg-clip-text text-transparent">
              Cost Savings Signals
            </span>
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mb-10 leading-relaxed">
            Your electronic components sourcing just got smarter. Unlock real-time pricing intelligence, 
            AI-powered predictions, and actionable savings across every part in your BOM — all in one 
            secure, compliant platform.
          </p>

          <div className="flex items-center gap-4 mb-16">
            <Button
              onClick={() => navigate('/cost-savings')}
              className="bg-green-600 hover:bg-green-700 text-white h-12 px-8 text-base font-semibold rounded-lg shadow-lg shadow-green-600/20"
              data-testid="hero-get-started-btn"
            >
              Start Saving Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="h-12 px-6 text-base border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg"
              data-testid="hero-demo-btn"
            >
              <Play className="w-4 h-4 mr-2" /> Watch Demo
            </Button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '4B+', label: 'Price Data Points', icon: Database },
              { value: '12%', label: 'Avg Cost Reduction', icon: TrendingDown },
              { value: '850+', label: 'Manufacturer Sources', icon: Globe },
              { value: '< 2s', label: 'Analysis Time', icon: Zap },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3 p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-xs text-gray-500">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">How Cost Savings Signals Work</h2>
            <p className="text-gray-500 max-w-lg mx-auto">Three steps to unlock savings across your entire electronic components portfolio</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: FileText,
                title: 'Input Your Data',
                desc: 'Upload a BOM or enter part details with your constraints — pricing, quantity, incoterms, lead time, country of origin, and alternate preferences.',
                color: 'green'
              },
              {
                step: '02',
                icon: Brain,
                title: 'AI Analyzes Billions',
                desc: 'Our AI agents scan 4B+ real price points, data pipes, and market signals to find where you sit in the pricing landscape and identify savings.',
                color: 'blue'
              },
              {
                step: '03',
                icon: TrendingDown,
                title: 'Get Actionable Savings',
                desc: 'See your cost position, price predictions, smart alternates, and total savings — with one-click assignment to your sourcing team.',
                color: 'green'
              }
            ].map((item) => (
              <Card key={item.step} className="border-gray-200 shadow-sm hover:shadow-md transition-shadow bg-white relative overflow-hidden group">
                <div className={`absolute top-0 left-0 w-1 h-full ${item.color === 'green' ? 'bg-green-500' : 'bg-[#0B3D91]'}`} />
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-bold text-gray-300 tracking-wider">STEP {item.step}</span>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${item.color === 'green' ? 'bg-green-50' : 'bg-blue-50'}`}>
                    <item.icon className={`w-6 h-6 ${item.color === 'green' ? 'text-green-600' : 'text-[#0B3D91]'}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-[#0B3D91]/10 text-[#0B3D91] border-0 text-xs font-medium px-3 py-1 mb-4">
              Live Demo
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">See It In Action</h2>
            <p className="text-gray-500 max-w-lg mx-auto">Watch how our platform turns your BOM data into real, actionable cost savings</p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center gap-2 mb-10">
            {[
              { id: 'overview', label: 'Platform Overview' },
              { id: 'ai', label: 'AI Agents & Predictions' },
              { id: 'savings', label: 'Cost Savings Flow' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`demo-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Video Placeholder — Ready for YouTube embed */}
          {/* To embed a real video, replace the inner div with:
              <iframe src="https://www.youtube.com/embed/YOUR_VIDEO_ID" ... />
          */}
          <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl overflow-hidden shadow-2xl aspect-video max-w-4xl mx-auto" data-testid="demo-video-container">
            {/* Animated grid overlay */}
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }} />

            {/* Floating data visualization elements */}
            <div className="absolute top-8 left-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3 opacity-60">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[10px] text-green-300 font-mono">LIVE DATA FEED</span>
              </div>
              <div className="text-[9px] text-gray-400 font-mono space-y-0.5">
                <div>4.2B price records indexed</div>
                <div>850+ supplier sources</div>
                <div>Updated: Real-time</div>
              </div>
            </div>

            <div className="absolute bottom-8 right-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3 opacity-60">
              <div className="text-[10px] text-blue-300 font-mono mb-1">AI AGENT STATUS</div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-[9px] text-gray-400">Price Scanner: Active</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-[9px] text-gray-400">Savings Detector: Active</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                <span className="text-[9px] text-gray-400">Alert Engine: Monitoring</span>
              </div>
            </div>

            {/* Center play button and text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center mb-6 cursor-pointer hover:bg-white/20 transition-all group" data-testid="demo-play-btn">
                <Play className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {activeTab === 'overview' && 'How the Platform Works'}
                {activeTab === 'ai' && 'AI Agents & Real-Time Predictions'}
                {activeTab === 'savings' && 'End-to-End Cost Savings Flow'}
              </h3>
              <p className="text-sm text-gray-400 max-w-md text-center">
                {activeTab === 'overview' && 'Billions of data points, intelligent data pipes, and AI-led cost saving signals — all working together for your procurement team.'}
                {activeTab === 'ai' && 'Our AI agents continuously scan real price points across 850+ sources and ping you with actual actionable savings opportunities.'}
                {activeTab === 'savings' && 'From BOM upload to quantified savings and team assignment — see how we turn data into dollars saved.'}
              </p>
              <p className="text-xs text-gray-500 mt-4">Video coming soon — click Get Started to try it live</p>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-6 left-[45%] flex items-center gap-2 opacity-60">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="absolute bottom-6 left-6 text-xs text-gray-500 font-mono">
              {activeTab === 'overview' && 'platform_demo_v2.mp4 — 3:42'}
              {activeTab === 'ai' && 'ai_agents_deep_dive.mp4 — 5:18'}
              {activeTab === 'savings' && 'savings_walkthrough.mp4 — 4:05'}
            </div>
          </div>

          {/* Key Capabilities Below Video */}
          <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-10">
            {[
              { icon: Database, label: 'Billions of Live Data Points', desc: 'Real pricing from 850+ sources' },
              { icon: Brain, label: 'AI-Powered Predictions', desc: 'Hold/buy signals with confidence' },
              { icon: Activity, label: 'Continuous Monitoring', desc: 'Agents ping you with savings' },
              { icon: Shield, label: 'Compliant & Secure', desc: 'SOC 2 Type II certified' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1">{item.label}</div>
                <div className="text-xs text-gray-500">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security-section" className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-green-100 text-green-700 border-0 text-xs font-medium px-3 py-1 mb-4">
                Enterprise-Grade Security
              </Badge>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Your Data is Safe, Confidential & Never Shared
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                We understand the sensitivity of your procurement data. Your pricing, BOMs, and supplier 
                information are used exclusively for your internal analysis and benchmarking. 
                We never share, sell, or expose your data to any third party — ever.
              </p>

              <div className="space-y-4">
                {[
                  { icon: ShieldCheck, title: 'End-to-End Encryption', desc: 'AES-256 encryption at rest and TLS 1.3 in transit' },
                  { icon: Lock, title: 'Zero Data Sharing', desc: 'Your data is never shared — used only for your internal analysis' },
                  { icon: Eye, title: 'Full Audit Trail', desc: 'Complete visibility into who accessed what, when' },
                  { icon: Users, title: 'Role-Based Access', desc: 'Granular permissions — each team member sees only what they need' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{item.title}</div>
                      <div className="text-xs text-gray-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications Grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { badge: 'ISO 27001', title: 'Information Security', desc: 'Certified management system for information security', icon: Shield },
                { badge: 'SOC 2 Type II', title: 'Service Organization', desc: 'Audited controls for security, availability & confidentiality', icon: Server },
                { badge: 'GDPR', title: 'Data Protection', desc: 'Full compliance with EU General Data Protection Regulation', icon: Lock },
                { badge: 'ISO 9001', title: 'Quality Management', desc: 'Certified quality management processes', icon: Award },
              ].map((cert) => (
                <Card key={cert.badge} className="border-gray-200 shadow-sm hover:shadow-md transition-shadow bg-white" data-testid={`cert-${cert.badge.replace(/\s+/g, '-').toLowerCase()}`}>
                  <CardContent className="p-5">
                    <div className="w-10 h-10 bg-[#0B3D91]/10 rounded-lg flex items-center justify-center mb-3">
                      <cert.icon className="w-5 h-5 text-[#0B3D91]" />
                    </div>
                    <Badge className="bg-gray-100 text-gray-700 border-0 text-[10px] font-bold mb-2">{cert.badge}</Badge>
                    <div className="text-sm font-semibold text-gray-900 mb-1">{cert.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{cert.desc}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Two Powerful Cost Reduction Engines</h2>
            <p className="text-gray-500 max-w-lg mx-auto">Whether you are optimizing a single part or an entire BOM — we have you covered</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Part Level */}
            <Card className="border-gray-200 shadow-sm hover:shadow-md transition-all bg-white group overflow-hidden">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mb-5 group-hover:bg-green-100 transition-colors">
                  <Cpu className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Part-Level Cost Reduction</h3>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                  Enter a single part number and see exactly where you stand in the market — your price percentile, 
                  prediction signals, and the best alternates with real savings potential.
                </p>
                <div className="space-y-2">
                  {['Price percentile positioning', 'AI hold/buy prediction', 'Smart alternate suggestions', 'Spot rate vs lead time analysis'].map((f) => (
                    <div key={f} className="flex items-center gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* BOM Level */}
            <Card className="border-gray-200 shadow-sm hover:shadow-md transition-all bg-white group overflow-hidden">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#0B3D91]/10 rounded-2xl flex items-center justify-center mb-5 group-hover:bg-[#0B3D91]/20 transition-colors">
                  <Layers className="w-7 h-7 text-[#0B3D91]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">BOM-Level Cost Reduction</h3>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                  Upload your full Bill of Materials and get a complete cost optimization analysis — 
                  total savings potential, component-by-component breakdown, and team-assignable actions.
                </p>
                <div className="space-y-2">
                  {['Full BOM savings analysis', 'Component-level breakdown', 'Volume-multiplied savings', 'Team assignment workflows'].map((f) => (
                    <div key={f} className="flex items-center gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-3.5 h-3.5 text-[#0B3D91] flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Unlock Real Savings?
          </h2>
          <p className="text-gray-400 mb-8 text-lg">
            Join leading OEMs and EMS companies already saving millions on their electronic components sourcing.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/cost-savings')}
              className="bg-green-600 hover:bg-green-700 text-white h-12 px-8 text-base font-semibold rounded-lg shadow-lg shadow-green-600/20"
              data-testid="cta-get-started-btn"
            >
              Get Started Free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="h-12 px-6 text-base border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg"
              data-testid="cta-signin-btn"
            >
              Sign In to Dashboard
            </Button>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8 text-xs text-gray-500">
            <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-500" /> No credit card required</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-500" /> SOC 2 Type II compliant</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-500" /> Results in seconds</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#0B3D91] rounded flex items-center justify-center">
                <span className="text-white font-bold text-[10px]">A</span>
              </div>
              <span className="text-[#0B3D91] font-semibold text-sm">ACCURIS</span>
            </div>
            <span className="text-gray-300">+</span>
            <div className="flex items-center gap-0.5">
              <span className="text-green-600 font-semibold text-sm">1BUY</span>
              <span className="text-gray-700 font-medium text-sm">.AI</span>
            </div>
          </div>
          <p className="text-xs text-gray-400">
            A joint product by Accuris & 1BUY.AI — AI-First Supply Chain Intelligence
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
